import { useEffect } from 'react'
import { useRouterState } from '@tanstack/react-router'
import { useT } from '../../i18n'
import { alertI18n } from '../../i18n/shared/alert'
import { useToastStore, isVisibleToRole, type Toast } from '../../lib/toast'
import type { Role } from '../../store/role'
import { cn } from '../../lib/utils'
import { PexIcon } from '../icons/PexIcon'
import type { PexIconName } from '../icons/pex-icon-registry'
import type { PexIconTone } from '../icons/pex-icon-types'

const VARIANT_STYLES: Record<
  Toast['variant'],
  { container: string; icon: PexIconName; tone: PexIconTone }
> = {
  success: {
    container: 'border-l-[var(--color-success)]',
    icon: 'success',
    tone: 'success',
  },
  error: {
    container: 'border-l-[var(--color-danger)]',
    icon: 'danger',
    tone: 'danger',
  },
  warning: {
    container: 'border-l-[var(--color-warning)]',
    icon: 'warning',
    tone: 'warning',
  },
  info: {
    container: 'border-l-[var(--color-info)]',
    icon: 'info',
    tone: 'info',
  },
}

function ToastItem({ toast }: { toast: Toast }) {
  const t = useT(alertI18n)
  const dismiss = useToastStore((s) => s.dismiss)
  const style = VARIANT_STYLES[toast.variant]

  useEffect(() => {
    if (toast.duration_ms <= 0) return
    const timer = setTimeout(() => dismiss(toast.id), toast.duration_ms)
    return () => clearTimeout(timer)
  }, [toast.id, toast.duration_ms, dismiss])

  return (
    <div
      role="status"
      className={cn(
        'pointer-events-auto flex items-start gap-3 rounded-[var(--radius-panel)] border border-l-[3px] border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3 text-[var(--color-text)] shadow-[var(--shadow-float)]',
        'min-w-72 max-w-sm',
        style.container,
      )}
    >
      <PexIcon
        name={style.icon}
        size="sm"
        weight="bold"
        tone={style.tone}
        className="mt-0.5"
      />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium leading-5">{toast.title}</div>
        {toast.description && (
          <div className="mt-0.5 text-xs leading-5 opacity-80">{toast.description}</div>
        )}
      </div>
      <button
        type="button"
        onClick={() => dismiss(toast.id)}
        className="flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-[var(--radius-control)] hover:bg-[var(--color-surface-muted)] focus-visible:outline-none focus-visible:shadow-[var(--shadow-focus)] sm:min-h-8 sm:min-w-8"
        aria-label={t.closeAriaLabel}
      >
        <PexIcon name="close" size="xs" tone="muted" />
      </button>
    </div>
  )
}

export function Toaster() {
  const toasts = useToastStore((s) => s.toasts)
  const pathname = useRouterState({ select: (s) => s.location.pathname })

  const activeRole: Role | '' = pathname.startsWith('/admin')
    ? 'admin'
    : pathname.startsWith('/seller')
      ? 'seller'
      : pathname.startsWith('/supply')
        ? 'supply'
        : ''

  const visible = toasts.filter((t) => isVisibleToRole(t, activeRole))
  if (visible.length === 0) return null

  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed top-3 sm:top-4 right-3 sm:right-4 left-3 sm:left-auto z-50 flex flex-col gap-2 safe-top safe-right"
    >
      {visible.map((t) => (
        <ToastItem key={t.id} toast={t} />
      ))}
    </div>
  )
}
